import {messages} from '@smartmixin/i18n';
import {Box, Modal} from '@mui/material';
import {KeyboardEvent, useState} from 'react';
import {useIntl} from 'react-intl';
import manifest from '../help-images.json';

type HelpImageProps = {
  src?: string;
  alt?: string;
};

// Generated at prebuild by scripts/generate-articles.js from the PNGs in
// public/help-images/ — explicit dimensions prevent layout shift.
const dimensions = manifest as Record<string, {w: number; h: number}>;

/**
 * Renderer for `![alt](…)` markdown images in help articles.
 *
 * The URL path picks the treatment:
 * - `/help-images/web/…`    → bordered card (rounded corners, soft shadow)
 * - `/help-images/mobile/…` → pre-framed iPhone PNG, rendered bare
 * - anything else           → plain image, no lightbox
 *
 * Click (or Enter/Space) opens a fullscreen lightbox. All non-portal markup
 * is span-based because react-markdown nests images inside <p> elements.
 */
const HelpImage = ({src = '', alt = ''}: HelpImageProps) => {
  const {formatMessage} = useIntl();
  const [open, setOpen] = useState(false);

  const variant = src.startsWith('/help-images/web/')
    ? 'web'
    : src.startsWith('/help-images/mobile/')
      ? 'mobile'
      : null;

  if (!variant) {
    return (
      <img
        data-testid="help-image-fallback"
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{maxWidth: '100%'}}
      />
    );
  }

  const size = dimensions[src];

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  };

  return (
    <>
      <Box
        component="span"
        data-testid={`help-image-${variant}`}
        role="button"
        tabIndex={0}
        aria-label={formatMessage(messages.common.zoomImage)}
        onClick={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'block',
          textAlign: 'center',
          my: 3,
          cursor: 'zoom-in',
          '& img': {
            maxWidth: variant === 'mobile' ? 360 : '100%',
            width: variant === 'mobile' ? '100%' : undefined,
            height: 'auto',
            verticalAlign: 'middle',
            ...(variant === 'web' && {
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 2,
            }),
          },
        }}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          width={size?.w}
          height={size?.h}
        />
      </Box>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box
          data-testid="help-image-lightbox"
          onClick={() => setOpen(false)}
          sx={{outline: 'none', cursor: 'zoom-out', lineHeight: 0}}>
          <img
            src={src}
            alt={alt}
            style={{
              maxWidth: '92vw',
              maxHeight: '92vh',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default HelpImage;
